"use client";

import LocaleLink from "@components/LocaleLink";
import { type Locale, localizePath } from "@lib/i18n/locale";
import { useHubSearch } from "@modules/hub/components/HubSearch";
import type { HubCategory } from "@modules/hub/lib/categories";
import { getHubPath, toSearchQuery } from "@modules/hub/lib/hubPath";
import { useLinkStatus } from "next/link";
import { type PropsWithChildren, useLayoutEffect, useRef } from "react";

// Where the chips sat on screen when one was chosen. The next page mounts a fresh set, and its
// hero has a different height, so without this the chips would jump under the visitor's finger.
let chosenAt: number | null = null;

const ChipFace = ({
  current,
  children,
}: PropsWithChildren<{ current: boolean }>) => {
  const { pending } = useLinkStatus();
  const active = current || pending;

  return (
    <span
      className={`inline-flex items-center h-[40px] px-3s rounded-full border text-body-2 transition-colors ${
        active
          ? "bg-primary-25 border-primary-800 text-primary-800 font-semibold"
          : "bg-neutral-0 border-neutral-50 text-neutral-425 font-book group-hover:border-primary-800"
      }`}
    >
      {children}
    </span>
  );
};

const CategoryChips = ({
  locale,
  categories,
  active,
  allLabel,
  label,
}: {
  locale: Locale;
  categories: Pick<HubCategory, "slug" | "title">[];
  active: string | null;
  allLabel: string;
  label: string;
}) => {
  const { query: search } = useHubSearch();
  const navRef = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    if (chosenAt === null || !navRef.current) {
      return;
    }

    // The page scrolls smoothly by default, and a correction that animates is a jump of its own.
    window.scrollBy({
      top: navRef.current.getBoundingClientRect().top - chosenAt,
      behavior: "instant",
    });
    chosenAt = null;
  }, []);

  return (
    <nav ref={navRef} aria-label={label}>
      <ul className="mt-2s flex flex-wrap gap-2xs list-none p-none m-none">
        {[null, ...categories].map((category) => {
          const slug = category?.slug ?? null;
          const current = slug === active;
          const path = localizePath(
            locale,
            getHubPath(locale, { category: slug, page: 1 }),
          );

          return (
            <li key={slug ?? ""}>
              <LocaleLink
                href={`${path}${toSearchQuery(locale, search)}`}
                scroll={false}
                aria-current={current ? "page" : undefined}
                data-tracking-event="Hub - Category"
                onClick={() => {
                  chosenAt = current
                    ? null
                    : (navRef.current?.getBoundingClientRect().top ?? null);
                }}
                className="group inline-flex rounded-full no-underline focusable"
              >
                <ChipFace current={current}>
                  {category?.title ?? allLabel}
                </ChipFace>
              </LocaleLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default CategoryChips;
